import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AppContext } from '@/lib/core/types';
import type { CheckoutContentResponseModel } from '@/services/platform/api/model';
import { MewsPaymentStep } from './MewsPaymentStep';
import { recallCheckout, reviveMewsBooking, rememberCheckout } from './session';
import type { MewsCheckoutState, MewsPaidState } from './types';

interface MewsCheckoutScreenProps {
  content: CheckoutContentResponseModel;
  context?: AppContext;
}

/**
 * The route-based entry to Mews payment: resolves which reservation is being paid for from router
 * state or session storage, then hands off to `MewsPaymentStep` for the payment itself.
 *
 * Used by the flows that have not (or cannot) author an inline payment step — they navigate here
 * with `mewsCheckout` state and this navigates back with `mewsPaid` once the money lands. A flow
 * that does author one renders `MewsPaymentStep` directly instead and never leaves itself, which is
 * why the purchase-building lives there and not here.
 *
 * The mirror image of `EventCheckoutScreen`: there the purchase does not exist before the money,
 * so a successful payment fabricates a placeholder. Here the reservation already exists
 * (`AwaitingPayment`, held) by the time this screen is reached — `handleBookNow` in the booking
 * flow only routes here when the reservation it just created needs paying for — so a successful
 * payment just needs to say so and hand the flow's own `booking` back, unchanged, for its
 * confirmation screen to render from.
 */
export function MewsCheckoutScreen({ content, context }: MewsCheckoutScreenProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Router state first, session storage as the fallback — see `EventCheckoutScreen` for why
  // (a redirect-based method or a plain refresh both destroy router state).
  const routedCheckout = (location.state as { mewsCheckout?: MewsCheckoutState } | null)?.mewsCheckout;
  const checkout = useMemo(() => routedCheckout ?? recallCheckout('mews'), [routedCheckout]);
  const booking = useMemo(() => (checkout ? reviveMewsBooking(checkout.booking) : undefined), [checkout]);

  useEffect(() => {
    if (routedCheckout) rememberCheckout({ kind: 'mews', ...routedCheckout });
  }, [routedCheckout]);

  const handleSucceeded = useCallback(() => {
    if (!checkout || !booking) return;
    const state: MewsPaidState = { reservationGuid: checkout.reservationGuid, booking };
    // `CheckoutScreen` already forgets the session before calling this.
    navigate(checkout.returnPath, { replace: true, state: { mewsPaid: state } });
  }, [checkout, booking, navigate]);

  // An absent `checkout` (a deep link or a refresh lost both the router state and the session) is
  // passed straight through rather than short-circuited here: `CheckoutScreen` owns that recovery
  // and bounces home, and duplicating the decision would only risk the two disagreeing.
  return (
    <MewsPaymentStep
      reservationGuid={checkout?.reservationGuid}
      amountMajor={checkout?.amountMajor}
      currencyCode={checkout?.currencyCode}
      booking={booking}
      returnPath={checkout?.returnPath}
      content={content}
      context={context}
      onSuccess={handleSucceeded}
    />
  );
}
